import { useGetTSPsQuery } from '../../services/edgeApi';
import { numberFormatter } from '../../helpers/formatters';
import { useTheme } from '../../helpers/hooks';
import { PointBadge } from '../../helpers/PointBadge';
import {
  selectAllPoints,
  selectTspOptions,
} from '../../map/slice/mapSelectors';
import { Contract } from '@/types';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { LoadingIndicator } from '../../helpers/LoadingIndicator';
import {
  MultiSelectButton,
  MultiSelectSearch,
} from '../../helpers/MultiSelect';
import { useContracts } from '../../hooks/useContracts';
import { InfiniteScrollObserver } from '../../helpers/InfiniteScrollObserver';
import { ContractTypeNumber } from '../../types/api/Contract';

export type ContractSelectionProps = {
  selectedContracts: Contract[];
  onChange: (contracts: Contract, checked: boolean) => void;
  onBulkChange?: (contracts: Contract[], checked: boolean) => void;
};

export const ContractSelection: React.FC<ContractSelectionProps> = ({
  selectedContracts,
  onChange,
  onBulkChange,
}) => {
  useGetTSPsQuery(undefined);
  const tspOptions = useSelector(selectTspOptions);
  const selectedContractsSet = useMemo(() => {
    const set = new Set<number>();
    selectedContracts.forEach((c) => set.add(Number(c.id)));
    return set;
  }, [selectedContracts]);
  const [types, setTypes] = useState<number[]>();
  const [tspIds, setTspIds] = useState<number[]>();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterUseInScenarios, setFilterUseInScenarios] = useState<
    boolean | undefined
  >(undefined);
  const [filterIsActiveOnDate, setFilterIsActiveOnDate] = useState<
    boolean | undefined
  >(undefined);

  const [toggleFilters, setToggleFilters] = useState<{
    onlySelected?: boolean;
    hasPoints?: boolean;
  }>({
    onlySelected: undefined,
    hasPoints: undefined,
  });

  const filterIds = useMemo(
    () =>
      toggleFilters.onlySelected ? Array.from(selectedContractsSet) : undefined,
    [toggleFilters.onlySelected, selectedContractsSet],
  );

  const { contracts, isLoading, isFetching, next } = useContracts({
    includeDetails: true,
    pageSize: 30,
    filters: {
      searchText: searchTerm,
      contractIds: filterIds,
      types,
      tspIds,
      hasPoints: toggleFilters.hasPoints,
      isActiveOnDate: filterIsActiveOnDate,
      useInScenarios: filterUseInScenarios,
    },
  });

  const points = useSelector(selectAllPoints);
  const [theme] = useTheme();

  const headers = useMemo(
    () => [
      <th key={'index'}>
        <div className="d-flex justify-content-center">
          <input
            type="checkbox"
            className="form-check-input mb-1"
            onChange={(evt) => {
              const checked = evt.target.checked;
              onBulkChange?.(contracts, checked);
            }}
          />
        </div>
      </th>,
      <th key={'k_id'}>Contract Number</th>,
      <th key={'name'}>Name</th>,
      <th key={'owner'}>Owner</th>,
      <th key={'tsp_name'}>TSP Name</th>,
      <th key={'receipt_points'}>Receipt Points</th>,
      <th key={'delivery_points'}>Delivery Points</th>,
      <th key={'type'}>Type</th>,
      <th key={'fuel_rate'}>Fuel Rate</th>,
      <th key={'commodity_rate'}>Commodity Rate</th>,
      <th key={'max_daily_quantity'}>Max TRP</th>,
      <th key={'start_date'}>Contract Start</th>,
      <th key={'end_date'}>Contract End</th>,
    ],
    [contracts, onBulkChange],
  );

  return (
    <div>
      <div className="mt-4 flex-row row">
        <div className="col">
          <MultiSelectButton
            title="Filters"
            options={[
              {
                id: 'onlySelected=true',
                name: 'Only Selected',
                disabled: selectedContracts.length === 0,
              },
              { id: 'hasPoints=true', name: 'Has Points', disabled: false },
            ]}
            markedFirst={false}
            keyExtractor={(option) => option.id}
            isMarked={({ id }) => {
              const [field, value] = id.split('=');
              return (
                toggleFilters[field as keyof typeof toggleFilters] ===
                JSON.parse(value)
              );
            }}
            isDisabled={(option) => option.disabled}
            onToggle={(option, isMarked) => {
              const [field, value] = option.id.split('=');
              const filterValue = isMarked ? JSON.parse(value) : null;
              setToggleFilters((prev) => {
                const newFilters = { ...prev };
                if (filterValue !== null) {
                  newFilters[field as keyof typeof newFilters] = filterValue;
                } else {
                  delete newFilters[field as keyof typeof newFilters];
                }
                return newFilters;
              });
            }}
            optionRenderer={(option) => option.name}
          />
        </div>
        <div className="col">
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            onChange={(evt) => setSearchTerm(evt.target.value)}
            value={searchTerm}
          />
        </div>
        <div className="col">
          <MultiSelectSearch
            options={[
              { value: ContractTypeNumber.FIRM, label: 'FIRM' },
              { value: ContractTypeNumber.IT, label: 'IT' },
            ]}
            onChange={(selected) =>
              setTypes(
                selected.length > 0 ? selected.map((s) => s.value) : undefined,
              )
            }
            placeholder="Contract Types"
          />
        </div>
        <div className="col">
          <MultiSelectSearch
            options={tspOptions}
            onChange={(selected) => {
              setTspIds(
                selected.length > 0
                  ? selected.map((s) => Number(s.value))
                  : undefined,
              );
            }}
            placeholder="Pipelines"
          />
        </div>
        <div className="col">
          <select
            className="form-select"
            value={
              filterUseInScenarios === undefined
                ? 'all'
                : filterUseInScenarios
                ? 'yes'
                : 'no'
            }
            onChange={(e) => {
              const value = e.target.value;
              setFilterUseInScenarios(
                value === 'all' ? undefined : value === 'yes',
              );
            }}
          >
            <option value="all">Use in Scenarios: All</option>
            <option value="yes">Use in Scenarios: Yes</option>
            <option value="no">Use in Scenarios: No</option>
          </select>
        </div>
        <div className="col">
          <select
            className="form-select"
            value={
              filterIsActiveOnDate === undefined
                ? 'all'
                : filterIsActiveOnDate
                ? 'yes'
                : 'no'
            }
            onChange={(e) => {
              const value = e.target.value;
              setFilterIsActiveOnDate(
                value === 'all' ? undefined : value === 'yes',
              );
            }}
          >
            <option value="all">Is Active: All</option>
            <option value="yes">Is Active: Yes</option>
            <option value="no">Is Active: No</option>
          </select>
        </div>
      </div>
      <table className="table">
        <thead
          style={{
            position: 'sticky',
            top: 0,
          }}
        >
          <tr>{headers}</tr>
        </thead>
        <tbody>
          {contracts?.length > 0 ? (
            contracts.map((contract) => {
              return (
                <tr key={contract.id.toString()}>
                  <td>
                    <label
                      role="button"
                      className="d-flex justify-content-center"
                      htmlFor={`contract-check-${contract.id.toString()}`}
                    >
                      <input
                        role="button"
                        id={`contract-check-${contract.id.toString()}`}
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedContractsSet.has(Number(contract.id))}
                        onChange={(evt) => {
                          onChange(contract, evt.target.checked);
                        }}
                      />
                    </label>
                  </td>
                  <td>{contract.k_id}</td>
                  <td style={{ maxWidth: '256px' }}>{contract.name}</td>
                  <td>{contract.organization_name ?? contract.owner}</td>
                  <td>{contract.tsp_name}</td>
                  <td>
                    {contract.receipt_volumes.map(
                      ({ point_id: pointId, volume }) => {
                        const point = points[pointId];
                        return (
                          <PointBadge
                            point={point}
                            key={`${contract.id}-receipt-point-${pointId}`}
                            extra_text={
                              volume ? `${numberFormatter(volume, 0)} dth` : ''
                            }
                            theme={theme}
                          />
                        );
                      },
                    )}
                  </td>
                  <td>
                    {contract.delivery_volumes.map(
                      ({ point_id: pointId, volume }) => {
                        const point = points[pointId];
                        return (
                          <PointBadge
                            point={point}
                            key={`${contract.id}-delivery-point-${pointId}`}
                            extra_text={
                              volume ? `${numberFormatter(volume, 0)} dth` : ''
                            }
                            theme={theme}
                          />
                        );
                      },
                    )}
                  </td>
                  <td>{contract.type}</td>
                  <td>{contract.fuel_rate_schedule_name}</td>
                  <td>{contract.commodity_rate_schedule_name}</td>
                  <td>{numberFormatter(contract.max_daily_quantity, 0)}</td>
                  <td>{new Date(contract.start_date).toLocaleDateString()}</td>
                  <td>{new Date(contract.end_date).toLocaleDateString()}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={headers.length} className="text-center">
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="d-flex justify-content-center my-3">
        <LoadingIndicator isLoading={isLoading || isFetching} />
      </div>
      <InfiniteScrollObserver onScrollIn={next} />
    </div>
  );
};
